import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useBuilding } from "../../context/BuildingContext";
import { journalApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import PageHeader from "../../components/common/PageHeader";
import { formatCurrency, formatDate } from "../../utils/formatters";
import { JournalEntry } from "../../modules/journalTypes";

const categoryLabels: Record<string, string> = {
  maintenance: "Maintenance",
  utilities: "Utilities",
  insurance: "Insurance",
  management: "Management",
  reserve: "Reserve Fund",
  serviceCharge: "Service Charge",
  other: "Other",
};

const JournalEntryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentBuilding } = useBuilding();

  const [entry, setEntry] = useState<JournalEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const fetchEntry = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await journalApi.getEntry(id, currentBuilding?.id);
        // Unwrap { entry: ... } shape returned when queried without buildingId
        const item: JournalEntry = data?.entry ?? data;
        if (!item?.id) {
          setError("Journal entry not found.");
        } else {
          setEntry(item);
        }
      } catch (err) {
        console.error("Error fetching journal entry:", err);
        setError("Failed to load journal entry. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchEntry();
  }, [id, currentBuilding?.id]);

  const handlePost = async () => {
    if (!entry || !currentBuilding?.id) return;
    try {
      setActionLoading(true);
      const result = await journalApi.postEntry(entry.id, currentBuilding.id);
      setEntry(result.entry || { ...entry, status: "posted" });
    } catch (err) {
      console.error("Error posting journal entry:", err);
      setError("Failed to post journal entry.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReverse = async () => {
    if (!entry || !currentBuilding?.id) return;
    try {
      setActionLoading(true);
      await journalApi.reverseEntry(entry.id, currentBuilding.id);
      navigate("/dashboard/journal");
    } catch (err) {
      console.error("Error reversing journal entry:", err);
      setError("Failed to reverse journal entry.");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <Spinner message="Loading journal entry..." />;
  }

  if (error) {
    return (
      <div className="">
        <ErrorAlert message={error} />
        <div className="mt-4">
          <Link
            to="/dashboard/journal"
            className="text-primary-600 hover:text-blue-900 text-sm font-medium"
          >
            &larr; Back to Journal
          </Link>
        </div>
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="">
        <ErrorAlert message="Journal entry not found." />
        <div className="mt-4">
          <Link
            to="/dashboard/journal"
            className="text-primary-600 hover:text-blue-900 text-sm font-medium"
          >
            &larr; Back to Journal
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <PageHeader
        title="Journal Entry"
        description={entry.description}
        actions={
          <div className="flex gap-2">
            {(entry.status || "posted") === "draft" && (
              <button
                onClick={handlePost}
                disabled={actionLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none disabled:opacity-50"
              >
                Post Entry
              </button>
            )}
            {(entry.status || "posted") === "posted" && !entry.reversalOf && (
              <button
                onClick={handleReverse}
                disabled={actionLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none disabled:opacity-50"
              >
                Reverse Entry
              </button>
            )}
            {(entry.status || "posted") === "draft" && (
              <Link
                to={`/dashboard/journal/edit/${entry.id}`}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
              >
                Edit
              </Link>
            )}
            <button
              onClick={() => navigate("/dashboard/journal")}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
            >
              &larr; Back
            </button>
          </div>
        }
      />

      <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Entry Details
          </h3>
        </div>

        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Date</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatDate(entry.date)}
              </dd>
            </div>

            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Description</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {entry.description}
              </dd>
            </div>

            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Type</dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    entry.type === "income"
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {entry.type === "income" ? "Income" : "Expense"}
                </span>
              </dd>
            </div>

            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                {(() => {
                  const status = entry.status || "posted";
                  const colors = {
                    draft: "bg-yellow-100 text-yellow-800",
                    posted: "bg-green-100 text-green-800",
                    reversed: "bg-red-100 text-red-800",
                  };
                  return (
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        colors[status as keyof typeof colors] ||
                        "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                  );
                })()}
              </dd>
            </div>

            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Amount</dt>
              <dd
                className={`mt-1 text-sm font-semibold sm:mt-0 sm:col-span-2 ${
                  entry.type === "income" ? "text-green-600" : "text-red-600"
                }`}
              >
                {formatCurrency(entry.amount / 100)}
              </dd>
            </div>

            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Category</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {categoryLabels[entry.category] ?? entry.category}
              </dd>
            </div>

            {entry.reference && (
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Reference</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {entry.reference}
                </dd>
              </div>
            )}

            {entry.linkedEntityId &&
              entry.linkedEntityType === "financialRecord" && (
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Linked Record
                  </dt>
                  <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                    <Link
                      to={`/dashboard/financial-records/${entry.linkedEntityId}`}
                      className="text-primary-600 hover:underline"
                    >
                      View Financial Record →
                    </Link>
                  </dd>
                </div>
              )}

            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Created</dt>
              <dd className="mt-1 text-sm text-gray-500 sm:mt-0 sm:col-span-2">
                {formatDate(entry.createdAt)} by {entry.createdBy}
              </dd>
            </div>

            {entry.reversalOf && (
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Reversal Of
                </dt>
                <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                  <Link
                    to={`/dashboard/journal/${entry.reversalOf}`}
                    className="text-primary-600 hover:underline"
                  >
                    View original entry →
                  </Link>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Journal Lines */}
      {entry.lines && entry.lines.length > 0 && (
        <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Journal Lines
            </h3>
          </div>
          <div className="border-t border-gray-200 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Code
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Description
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Debit
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Credit
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {entry.lines.map((line) => (
                  <tr key={line.id}>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">
                      {line.nominalCode}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {line.categoryName || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {line.description || "-"}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                      {line.debit ? formatCurrency(line.debit / 100) : ""}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                      {line.credit ? formatCurrency(line.credit / 100) : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr className="border-t-2 border-gray-300">
                  <td
                    colSpan={3}
                    className="px-4 py-3 text-sm font-medium text-gray-900"
                  >
                    Total
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                    {formatCurrency(
                      entry.lines.reduce((s, l) => s + (l.debit || 0), 0) / 100,
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                    {formatCurrency(
                      entry.lines.reduce((s, l) => s + (l.credit || 0), 0) /
                        100,
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default JournalEntryDetail;
